// src/components/Workspace/BusinessProfileManagement.js - Improved ownership checking
import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import InviteCollaborator from '../Invitation/InviteCollaborator';
import { doc, updateDoc, deleteField, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

function BusinessProfileManagement() {
  const { user, activeBusinessProfileId, businessProfiles, checkProfileAccess } = useUserContext();
  const [activeProfile, setActiveProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberDetails, setMemberDetails] = useState({});
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Find the active profile
  useEffect(() => {
    if (businessProfiles && activeBusinessProfileId) {
      const profile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      setActiveProfile(profile || null);
      
      // Extract members if available
      if (profile && profile.members) {
        console.log('🔍 DEBUG - Profile members:', profile.members);
        console.log('🔍 DEBUG - Current user ID:', user?.uid);
        console.log('🔍 DEBUG - Profile creator:', profile.userId);
        console.log('🔍 DEBUG - Current user role in members:', profile.members[user?.uid]);
        console.log('🔍 DEBUG - IsOwner from checkProfileAccess:', checkProfileAccess(activeBusinessProfileId));
        
        const membersList = Object.entries(profile.members).map(([userId, role]) => {
          // CORRECTION: Check if the user is the creator of the profile
          // OR is marked as owner in the members field
          const isCreator = profile.userId === user?.uid;
          const isMarkedAsOwner = role === 'owner';
          // If it's the current user AND they are creator OR marked as owner
          const shouldBeOwner = userId === user?.uid && (isCreator || isMarkedAsOwner);
          
          return {
            userId,
            role: shouldBeOwner ? 'owner' : role, // Correct the role if needed
            isCurrentUser: userId === user?.uid
          };
        });
        setMembers(membersList);
        
        // Fetch member details
        const memberIds = membersList.map(member => member.userId);
        fetchMemberDetails(memberIds);
      } else if (profile && !profile.members && profile.userId === user?.uid) {
        // IMPROVED: If members field is missing but user is creator, fix it
        console.log('🔍 DEBUG - Missing members field, initializing it');
        initializeMembersField(profile);
      } else {
        setMembers([]);
        setMemberDetails({});
      }
    }
  }, [businessProfiles, activeBusinessProfileId, user]);
  
  // NEW: Function to initialize the members field if it's missing
  const initializeMembersField = async (profile) => {
    try {
      if (!user || !profile) return;
      
      const profileRef = doc(db, 'users', user.uid, 'businessProfiles', profile.id);
      
      // Add the current user as owner
      await updateDoc(profileRef, {
        members: { [user.uid]: 'owner' }
      });
      
      console.log('🔍 DEBUG - Successfully initialized members field');
      
      // Set local state to reflect the change
      setMembers([{
        userId: user.uid,
        role: 'owner',
        isCurrentUser: true
      }]);
    } catch (error) {
      console.error('Error initializing members field:', error);
      setError('Failed to initialize workspace permissions. Please try again.');
    }
  };
  
  // IMPROVED: More robust ownership determination
  // Checks if the user is an owner by being either the creator of the profile or having the owner role
  const determineIsOwner = () => {
    if (!activeProfile || !user) return false;
    
    // Check if the user is the creator of the profile
    const isCreator = activeProfile.userId === user.uid;
    
    // Check if the user has the owner role in the members field
    const isMarkedAsOwner = activeProfile.members && 
                           activeProfile.members[user.uid] === 'owner';
    
    // Check if it's a default profile (which implies ownership)
    const isDefaultProfile = activeProfile.isDefault === true;
    
    // If they are the creator OR marked as owner OR it's a default profile, consider them as owner
    return isCreator || isMarkedAsOwner || isDefaultProfile;
  };
  
  // Use the improved logic to determine if user is owner
  const isOwner = determineIsOwner();
  
  // Function to fetch member details from Firebase
  const fetchMemberDetails = async (memberIds) => {
    if (!memberIds || memberIds.length === 0) return {};
    
    setLoadingMembers(true);
    const details = {};
    
    try {
      const promises = memberIds.map(async (userId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            details[userId] = {
              email: userData.email || 'Email não encontrado',
              name: userData.name || userData.displayName || 'Usuário',
              avatar: userData.photoURL || null
            };
          } else {
            details[userId] = {
              email: 'Usuário não encontrado',
              name: 'Usuário desconhecido',
              avatar: null
            };
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          details[userId] = {
            email: 'Erro ao carregar email',
            name: 'Erro ao carregar',
            avatar: null
          };
        }
      });
      
      await Promise.all(promises);
      setMemberDetails(details);
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      setLoadingMembers(false);
    }
  };
  
  // Handle removing a member
  const handleRemoveMember = async (userId) => {
    if (!activeProfile || !userId || !isOwner) return;
    
    try {
      setError('');
      setSuccess('');
      setIsRemoving(true);
      setSelectedMember(userId);
      
      // Reference to the profile document
      const profileRef = doc(db, `users/${activeProfile.userId || user.uid}/businessProfiles/${activeProfile.id}`);
      
      // Create an object for the update
      const updates = {
        [`members.${userId}`]: deleteField(),
        updatedAt: new Date()
      };
      
      // Update the document
      await updateDoc(profileRef, updates);
      
      // Update the local state
      setMembers(prevMembers => prevMembers.filter(member => member.userId !== userId));
      
      setSuccess(`Colaborador removido com sucesso.`);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Falha ao remover colaborador. Por favor, tente novamente.');
    } finally {
      setIsRemoving(false);
      setSelectedMember(null);
    }
  };
  
  if (!activeProfile) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum workspace selecionado</h3>
          <p className="text-gray-500">
            Selecione um workspace para gerenciar seus colaboradores.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="overflow-hidden">
      {/* Header section */}
      <div className="px-6 py-5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Membros da Equipe</h2>
              <p className="text-sm text-gray-600">Gerencie o acesso ao seu workspace</p>
            </div>
          </div>
          
          {isOwner && (
            <button
              onClick={() => setShowInvite(!showInvite)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {showInvite ? 'Cancelar Convite' : 'Convidar Membro'}
            </button>
          )}
        </div>
      </div>
      
      {/* Alert messages */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-red-800">Erro</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-green-800">Sucesso</h4>
              <p className="text-sm text-green-700 mt-1">{success}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Invite form */}
      {showInvite && isOwner && (
        <div className="mx-6 mt-4 mb-4 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-blue-900 mb-2">Convidar Novo Colaborador</h3>
              <p className="text-sm text-blue-700 mb-4">
                Gere um link seguro para convidar alguém a colaborar neste workspace.
                O link de convite expirará em 24 horas.
              </p>
              <InviteCollaborator 
                profileId={activeProfile.id} 
                onInviteSent={() => {
                  setSuccess('Link de convite gerado com sucesso!');
                }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Members table */}
      <div className="px-6 py-4">
        {members.length > 0 ? (
          <div className="overflow-hidden shadow-sm rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Função
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.userId} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 avatar-letter text-white font-semibold text-sm leading-none">
                            {loadingMembers ? '...' : (memberDetails[member.userId]?.email?.charAt(0).toUpperCase() || member.userId.charAt(0).toUpperCase())}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {loadingMembers ? (
                              <div className="animate-pulse bg-gray-200 h-4 w-32 rounded"></div>
                            ) : (
                              <>
                                {memberDetails[member.userId]?.email || 'Carregando...'}
                                {member.isCurrentUser && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Você
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {loadingMembers ? (
                              <div className="animate-pulse bg-gray-200 h-3 w-24 rounded mt-1"></div>
                            ) : (
                              `ID: ${member.userId.substring(0, 8)}...`
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium leading-none ${
                        member.role === 'owner' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          member.role === 'owner' ? 'bg-purple-400' : 'bg-blue-400'
                        }`}></div>
                        {member.role === 'owner' ? 'Proprietário' : 'Colaborador'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></div>
                        Ativo
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!member.isCurrentUser && member.role !== 'owner' && isOwner && (
                        <button
                          onClick={() => handleRemoveMember(member.userId)}
                          disabled={isRemoving && selectedMember === member.userId}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 hover:border-red-400 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isRemoving && selectedMember === member.userId ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-red-700" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Removendo...
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Remover
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum membro encontrado</h3>
            <p className="text-gray-500 mb-4">Comece adicionando membros à sua equipe para colaborar.</p>
            {/* Botão "Convidar Primeiro Membro" removido */}
          </div>
        )}
      </div>
      
      {/* Info footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">Sobre as permissões de workspace</p>
            <p>Como <span className="font-medium text-blue-600">proprietário</span>, você pode convidar novos membros e gerenciar o acesso ao workspace.</p>
            <p className="mt-1">Membros com função <span className="font-medium text-blue-600">colaborador</span> podem visualizar e editar dados, mas não podem gerenciar outros membros.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessProfileManagement;